import React, { useEffect, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "growing", label: "Growing" },
    { value: "harvested", label: "Harvested" },
    { value: "dead", label: "Dead" },
];

const statusLabel = (s) => {
    if (s === "growing") return "Growing";
    if (s === "harvested") return "Harvested";
    if (s === "dead") return "Dead";
    return s || "-";
};

const PlantsReport = () => {
    const [lands, setLands] = useState([]);
    const [sections, setSections] = useState([]);
    const [plantCats, setPlantCats] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activelands.php')
            .then((res) => { if (mounted) setLands(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        api.get('/masterdropdown/listof_activesections.php')
            .then((res) => { if (mounted) setSections(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        api.get('/masterdropdown/listof_activeplantcategories.php')
            .then((res) => { if (mounted) setPlantCats(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Plants Register Report"
            subtitle="All registered plants with location and current status"
            breadcrumb={["Reports", "Farm Setup"]}
            fetchUrl="/reports/farm/plants.php"
            rowKey="plant_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 3, placeholder: "Plant code, category" },
                { type: "select", key: "land_id", label: "Land", col: 3, options: lands, showSearch: true },
                { type: "select", key: "section_id", label: "Section", col: 3, options: sections, showSearch: true },
                { type: "select", key: "pc_id", label: "Plant Category", col: 3, options: plantCats, showSearch: true },
                { type: "select", key: "current_status", label: "Status", col: 2, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "Planting Date", col: 4 },
            ]}
            columns={[
                { title: 'Plant Code', dataIndex: 'plant_code', key: 'plant_code', width: 180, sorter: (a, b) => (a.plant_code || '').localeCompare(b.plant_code || '') },
                { title: 'Plant Category', dataIndex: 'plant_category', key: 'plant_category', width: 130, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Land', dataIndex: 'land_name', key: 'land_name', width: 160, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Section', dataIndex: 'section_name', key: 'section_name', width: 160, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Field', dataIndex: 'field_code', key: 'field_code', width: 120, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Planting Date', dataIndex: 'planting_date', key: 'planting_date', width: 115, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.planting_date).valueOf() - dayjs(b.planting_date).valueOf() },
                {
                    title: 'Status', dataIndex: 'current_status', key: 'current_status', width: 110,
                    render: (s) => {
                        const color = s === "growing" ? "#52c41a" : (s === "harvested" ? "#1677ff" : "#ff4d4f");
                        return <span style={{ color, fontWeight: 600 }}>{statusLabel(s)}</span>;
                    },
                },
            ]}
            pdf={{
                title: "PLANTS REGISTER REPORT",
                subtitle: "All registered plants with location and current status",
                columns: [
                    { header: "Plant Code" }, { header: "Plant Category" }, { header: "Land" },
                    { header: "Section" }, { header: "Field" }, { header: "Planting Date" }, { header: "Status" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.plant_code || "-",
                        r.plant_category || "-",
                        r.land_name || "-",
                        r.section_name || "-",
                        r.field_code || "-",
                        dayjs(r.planting_date).format("YYYY-MM-DD"),
                        statusLabel(r.current_status),
                    ]),
                summary: (rows) => [
                    { label: "Total Plants:", value: String(rows.length) },
                ],
            }}
            excel={{
                filename: "plants-register-report",
                sheetName: "Plants Register",
                columns: [
                    { title: "Plant Code", dataIndex: "plant_code", width: 20 },
                    { title: "Plant Category", dataIndex: "plant_category", width: 16 },
                    { title: "Land", dataIndex: "land_name", width: 20 },
                    { title: "Section", dataIndex: "section_name", width: 20 },
                    { title: "Field", dataIndex: "field_code", width: 14 },
                    { title: "Planting Date", dataIndex: "planting_date", width: 13 },
                    { title: "Status", dataIndex: "current_status", width: 12 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        current_status: statusLabel(r.current_status),
                        planting_date: dayjs(r.planting_date).format("YYYY-MM-DD"),
                    })),
            }}
        />
    );
};

export default PlantsReport;
